import {
  Button,
  Icon,
  InlineMessage,
  Loader,
  Typography,
} from "@saviynt/design-system";
import { SaviyntLogo } from "@repo/ui";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";

type PageState = "loading" | "error" | "test-success" | "test-failure";

const SSO_TEST_PENDING_KEY = "sso-test-pending";
const SSO_TEST_MAX_AGE_MS = 5 * 60 * 1000; // 5 minutes
const BROADCAST_CHANNEL_NAME = "sso-test-result";
const BROADCAST_MESSAGE_TYPE = "SSO_TEST_RESULT";

/**
 * Checks if a test is pending (localStorage flag set within the last 5 minutes).
 */
function isTestPending(): boolean {
  const value = localStorage.getItem(SSO_TEST_PENDING_KEY);
  if (!value) return false;
  const timestamp = parseInt(value, 10);
  if (isNaN(timestamp)) return false;
  return Date.now() - timestamp < SSO_TEST_MAX_AGE_MS;
}

/**
 * Broadcasts the test result to the parent page via BroadcastChannel.
 */
function broadcastTestResult(success: boolean, message?: string) {
  if (typeof BroadcastChannel === "undefined") return;
  const channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
  channel.postMessage({
    type: BROADCAST_MESSAGE_TYPE,
    payload: { success, message, timestamp: Date.now() },
  });
  channel.close();
}

export default function SSOCallbackPage() {
  const { loginWithSSO } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const intl = useIntl();
  const [pageState, setPageState] = useState<PageState>("loading");
  const [errorMessage, setErrorMessage] = useState("");

  const isDark = theme === "dark";

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    const error = params.get("error");
    const errorDescription = params.get("error_description");

    const testMode = isTestPending();

    // Always clear the test flag (whether success or failure)
    localStorage.removeItem(SSO_TEST_PENDING_KEY);

    // Handle error from IdP
    if (error) {
      const msg = errorDescription || error;
      if (testMode) {
        broadcastTestResult(false, msg);
        setErrorMessage(msg);
        setPageState("test-failure");
      } else {
        setErrorMessage(msg);
        setPageState("error");
      }
      return;
    }

    if (!code || !state) {
      const msg = intl.formatMessage({
        id: "KDTIvP",
        defaultMessage:
          "Invalid SSO callback. Required parameters are missing.",
        description:
          "Error when SSO callback URL is missing code or state parameters",
      });
      if (testMode) {
        broadcastTestResult(false, msg);
        setErrorMessage(msg);
        setPageState("test-failure");
      } else {
        setErrorMessage(msg);
        setPageState("error");
      }
      return;
    }

    if (testMode) {
      // TEST MODE: The IdP redirected back with a valid code+state,
      // which proves the SSO configuration is correct. We do NOT call
      // loginWithSSO here because it stores tokens in localStorage
      // (shared across tabs), which would overwrite and then destroy
      // the admin's active session in the parent tab.
      broadcastTestResult(true);
      setPageState("test-success");
    } else {
      // NORMAL MODE: Exchange code and store tokens (existing flow)
      loginWithSSO(code, state)
        .then((response) => {
          if (response.accessToken && response.refreshToken) {
            navigate({ to: "/" });
          } else {
            setErrorMessage(
              response.message ||
                intl.formatMessage({
                  id: "RuacCA",
                  defaultMessage: "SSO login failed. Please try again.",
                  description: "Error when SSO login fails",
                }),
            );
            setPageState("error");
          }
        })
        .catch(() => {
          setErrorMessage(
            intl.formatMessage({
              id: "RuacCA",
              defaultMessage: "SSO login failed. Please try again.",
              description: "Error when SSO login fails",
            }),
          );
          setPageState("error");
        });
    }
  }, []);

  const background = (
    <>
      <div
        className="absolute size-[1200px] pointer-events-none"
        style={{ bottom: "-571px", left: "-601px" }}
      >
        <div
          className="absolute inset-0 rounded-full blur-[200px]"
          style={{
            background: isDark
              ? "radial-gradient(circle, rgba(0,80,255,0.4) 0%, transparent 70%)"
              : "radial-gradient(circle, rgba(100,80,255,0.3) 0%, transparent 70%)",
          }}
        />
      </div>
      <div
        className="absolute size-[1200px] pointer-events-none"
        style={{ top: "-629px", right: "-599px" }}
      >
        <div
          className="absolute inset-0 rounded-full blur-[200px]"
          style={{
            background: isDark
              ? "radial-gradient(circle, rgba(0,200,50,0.4) 0%, transparent 70%)"
              : "radial-gradient(circle, rgba(0,220,80,0.4) 0%, transparent 70%)",
          }}
        />
      </div>
    </>
  );

  // Loading state
  if (pageState === "loading") {
    return (
      <div
        className={`w-full h-screen relative overflow-hidden flex items-center justify-center ${
          isDark ? "bg-black" : "bg-white"
        }`}
      >
        {background}
        <Loader kind="loop" format="main" size="medium" color="brand" />
      </div>
    );
  }

  // Test Success
  if (pageState === "test-success") {
    return (
      <div
        className={`w-full h-screen relative overflow-hidden flex items-center justify-center ${
          isDark ? "bg-black" : "bg-white"
        }`}
      >
        {background}
        <div
          className={`relative z-10 w-[560px] px-[100px] pt-[60px] pb-[80px] rounded-[20px] flex flex-col items-center ${
            isDark
              ? "bg-[rgba(255,255,255,0.07)] drop-shadow-[0px_30px_30px_rgba(0,0,0,0.15)]"
              : "bg-white drop-shadow-[0px_30px_30px_rgba(0,0,0,0.15)]"
          }`}
        >
          <SaviyntLogo
            variant={isDark ? "dark" : "light"}
            className="h-12 mb-8"
          />
          <div
            className="flex flex-col items-center gap-4 text-center"
            role="alert"
          >
            <Icon kind="CheckCircleFilled" size="large" color="success-100" />
            <Typography kind="h2" color="--color-foreground-neutral-bold">
              <FormattedMessage
                id="TypAp9"
                defaultMessage="SSO Test Successful!"
                description="Title shown when SSO test connection succeeds"
              />
            </Typography>
            <Typography kind="body1" color="--color-foreground-neutral-subtle">
              <FormattedMessage
                id="ImWIXk"
                defaultMessage="Your Identity Provider is configured correctly. You may close this window and return to the Admin settings."
                description="Message shown when SSO test connection succeeds"
              />
            </Typography>
          </div>
          <div className="mt-8">
            <Button kind="filled" size="medium" onClick={() => window.close()}>
              <FormattedMessage
                id="cDwISk"
                defaultMessage="Close Window"
                description="Button to close the SSO test callback window"
              />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Test Failure
  if (pageState === "test-failure") {
    return (
      <div
        className={`w-full h-screen relative overflow-hidden flex items-center justify-center ${
          isDark ? "bg-black" : "bg-white"
        }`}
      >
        {background}
        <div
          className={`relative z-10 w-[560px] px-[100px] pt-[60px] pb-[80px] rounded-[20px] flex flex-col items-center ${
            isDark
              ? "bg-[rgba(255,255,255,0.07)] drop-shadow-[0px_30px_30px_rgba(0,0,0,0.15)]"
              : "bg-white drop-shadow-[0px_30px_30px_rgba(0,0,0,0.15)]"
          }`}
        >
          <SaviyntLogo
            variant={isDark ? "dark" : "light"}
            className="h-12 mb-8"
          />
          <div
            className="flex flex-col items-center gap-4 text-center"
            role="alert"
          >
            <Icon
              kind="AlertCriticalFilled"
              size="large"
              color="critical-100"
            />
            <Typography kind="h2" color="--color-foreground-neutral-bold">
              <FormattedMessage
                id="jFFjob"
                defaultMessage="SSO Test Failed"
                description="Title shown when SSO test connection fails"
              />
            </Typography>
            <Typography kind="body1" color="--color-foreground-neutral-subtle">
              {errorMessage}
            </Typography>
          </div>
          <div className="mt-8">
            <Button kind="filled" size="medium" onClick={() => window.close()}>
              <FormattedMessage
                id="cDwISk"
                defaultMessage="Close Window"
                description="Button to close the SSO test callback window"
              />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Normal SSO Error (non-test)
  return (
    <div
      className={`w-full h-screen relative overflow-hidden flex items-center justify-center ${
        isDark ? "bg-black" : "bg-white"
      }`}
    >
      {background}
      <div
        className={`relative z-10 w-[560px] px-[100px] pt-[60px] pb-[80px] rounded-[20px] flex flex-col items-center ${
          isDark
            ? "bg-[rgba(255,255,255,0.07)] drop-shadow-[0px_30px_30px_rgba(0,0,0,0.15)]"
            : "bg-white drop-shadow-[0px_30px_30px_rgba(0,0,0,0.15)]"
        }`}
      >
        <div className="w-full flex flex-col">
          <div className="text-center flex flex-col justify-start items-center">
            <SaviyntLogo
              variant={isDark ? "dark" : "light"}
              className="h-12 mb-8"
            />
            <div className="flex flex-col gap-2 mb-6">
              <Typography kind="h3">
                <FormattedMessage
                  id="hIUqsJ"
                  defaultMessage="Authentication Failed"
                  description="SSO error page heading"
                />
              </Typography>
              <Typography kind="body3">
                <FormattedMessage
                  id="aCwkKs"
                  defaultMessage="We were unable to complete your sign-in."
                  description="SSO error page subtitle"
                />
              </Typography>
            </div>
          </div>
          <div className="flex flex-col gap-4">
            <InlineMessage
              text={errorMessage}
              colorTheme="critical"
              kind="background"
              size="small"
              leftIcon={<Icon kind="AlertCriticalFilled" />}
            />
            <Button
              kind="ghost"
              size="large"
              className="w-full justify-center"
              onClick={() => {
                window.location.href = "/";
              }}
            >
              <FormattedMessage
                id="mpAX9d"
                defaultMessage="Back to Login"
                description="Button to return to the login page after an SSO error"
              />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
